import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { WizardStepper } from "../WizardStepper";
import { SuccessToast } from "../../SuccessToast";
import { registerProvider } from "../../../core/registration/registration";
import type {
	CompanyState,
	FieldErrors,
	PersonalState,
	DayAvailability,
} from "../../../core/types/provider-registration";
import { StepPersonal } from "./StepPersonal";
import { StepCompany } from "./StepCompany";
import { StepAvailability } from "./StepAvailability";
import {
	createDefaultAvailability,
	time12ToMinutes,
} from "../../../core/utils/provider-registration";
import { getPhoneValidationError } from "../../../core/utils/phone";
import type { ProviderRegistrationFormProps } from "../../../core/types/tsx/components/wizard/provider-registration/ProviderRegistrationForm.types";

function generatePassword(length = 14): string {
	const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
	const values = new Uint32Array(length);
	crypto.getRandomValues(values);
	let out = "";
	for (let i = 0; i < values.length; i += 1) {
		out += alphabet[values[i] % alphabet.length];
	}
	// Ensure at least one digit for existing password rules.
	if (!/\d/.test(out)) out = out.slice(0, -1) + "7";
	return out;
}

export function ProviderRegistrationForm({
	mode = "public",
	variant = "page",
	onClose,
	onCreated,
	successRedirectTo,
}: ProviderRegistrationFormProps) {
	const navigate = useNavigate();
	const isAdminMode = mode === "admin";

	const [toastOpen, setToastOpen] = useState(false);
	const [activationScreenOpen, setActivationScreenOpen] = useState(false);
	const [serverError, setServerError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [step, setStep] = useState<0 | 1 | 2>(0);
	const [showPassword, setShowPassword] = useState(false);
	const [accepted, setAccepted] = useState(false);
	const [errors, setErrors] = useState<FieldErrors>({});
	const redirectTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (!activationScreenOpen || !successRedirectTo) return;

		redirectTimeoutRef.current = window.setTimeout(() => {
			navigate(successRedirectTo);
		}, 8000);

		return () => {
			if (redirectTimeoutRef.current !== null) {
				window.clearTimeout(redirectTimeoutRef.current);
				redirectTimeoutRef.current = null;
			}
		};
	}, [activationScreenOpen, navigate, successRedirectTo]);

	// Persistencia local
	const LOCAL_KEY = "provider-registration-wizard";
	const LEGACY_PASSWORD_KEYS = [
		"provider-registration-password",
		"provider-registration-confirmPassword",
		`${LOCAL_KEY}-password`,
		`${LOCAL_KEY}-confirmPassword`,
	];
	const defaultPersonalState: PersonalState = {
		nombre: "",
		apellido: "",
		email: "",
		telefono: "",
		password: "",
		confirmPassword: "",
		direccion: "",
	};
	const [personal, setPersonal] = useState<PersonalState>(() => {
		const saved = localStorage.getItem(LOCAL_KEY);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (parsed.personal) {
					return {
						...defaultPersonalState,
						...parsed.personal,
						password: "",
						confirmPassword: "",
					};
				}
			} catch {
				// Ignore invalid persisted wizard payload.
			}
		}
		return defaultPersonalState;
	});

	const [company, setCompany] = useState<CompanyState>(() => {
		const saved = localStorage.getItem(LOCAL_KEY);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				return (
					parsed.company || {
						nombreEmpresa: "",
						descripcion: "",
						categoryIds: [],
					}
				);
			} catch {
				// Ignore invalid persisted wizard payload.
			}
		}
		return {
			nombreEmpresa: "",
			descripcion: "",
			categoryIds: [],
		};
	});

	const [availability, setAvailability] = useState<DayAvailability[]>(() => {
		const saved = localStorage.getItem(LOCAL_KEY);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				return parsed.availability || createDefaultAvailability();
			} catch {
				// Ignore invalid persisted wizard payload.
			}
		}
		return createDefaultAvailability();
	});

	// Cleanup legacy sensitive keys and sanitize old wizard payloads that still contain passwords.
	useEffect(() => {
		LEGACY_PASSWORD_KEYS.forEach((key) => localStorage.removeItem(key));

		const saved = localStorage.getItem(LOCAL_KEY);
		if (!saved) return;

		try {
			const parsed = JSON.parse(saved);
			const personalSaved = parsed?.personal;
			if (!personalSaved || typeof personalSaved !== "object") return;

			const hasPasswordFields =
				"password" in personalSaved || "confirmPassword" in personalSaved;
			if (!hasPasswordFields) return;

			const {
				password: _password,
				confirmPassword: _confirmPassword,
				...sanitizedPersonal
			} = personalSaved;
			void _password;
			void _confirmPassword;

			localStorage.setItem(
				LOCAL_KEY,
				JSON.stringify({ ...parsed, personal: sanitizedPersonal }),
			);
		} catch {
			// Ignore invalid persisted wizard payload.
		}
	}, []);

	// Guardar cambios en localStorage
	useEffect(() => {
		const {
			password: _password,
			confirmPassword: _confirmPassword,
			...persistedPersonal
		} = personal;
		void _password;
		void _confirmPassword;

		localStorage.setItem(
			LOCAL_KEY,
			JSON.stringify({ personal: persistedPersonal, company, availability }),
		);
	}, [personal, company, availability]);

	const inputBase =
		"w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary";
	const inputWithIcon =
		"w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary";

	const errorText = "mt-1 text-xs text-red-600";
	const clearError = (key: string) => {
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const { [key]: _omit, ...rest } = prev;
			void _omit;
			return rest;
		});
	};

	const validateStep = (currentStep: 0 | 1 | 2) => {
		const nextErrors: FieldErrors = {};
		if (currentStep === 0) {
			const nombre = personal.nombre.trim();
			const apellido = personal.apellido.trim();
			const email = personal.email.trim();
			const telefono = personal.telefono.trim();
			const password = personal.password.trim();
			const confirmPassword = personal.confirmPassword.trim();
			const direccion = personal.direccion.trim();

			if (!nombre) nextErrors.nombre = "Este campo es obligatorio";
			else if (nombre.length < 3)
				nextErrors.nombre = "Debe tener al menos 3 caracteres";
			else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(nombre))
				nextErrors.nombre = "Solo se permiten letras en el nombre";
			if (!apellido) nextErrors.apellido = "Este campo es obligatorio";
			else if (apellido.length < 3)
				nextErrors.apellido = "Debe tener al menos 3 caracteres";
			else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(apellido))
				nextErrors.apellido = "Solo se permiten letras en el apellido";
			if (!email) nextErrors.email = "Este campo es obligatorio";
			else if (!/^\S+@\S+\.\S+$/.test(email)) nextErrors.email = "Correo inválido";
			const phoneError = getPhoneValidationError(telefono, { required: true });
			if (phoneError) nextErrors.telefono = phoneError;
			if (!isAdminMode) {
				if (!password) nextErrors.password = "Este campo es obligatorio";
				else if (password.length < 8)
					nextErrors.password = "Debe tener al menos 8 caracteres";
				else if (!/\d/.test(password))
					nextErrors.password = "Debe incluir al menos un número";
				if (!confirmPassword)
					nextErrors.confirmPassword = "Este campo es obligatorio";
				if (password && confirmPassword && password !== confirmPassword) {
					nextErrors.confirmPassword = "Las contraseñas no coinciden";
				}
			}
			if (!direccion) nextErrors.direccion = "Este campo es obligatorio";
			else if (direccion.length < 6)
				nextErrors.direccion = "Describe una dirección más precisa";
		}

		if (currentStep === 1) {
			const descripcion = company.descripcion.trim();
			const categoryCount = company.categoryIds.length;
			if (categoryCount === 0)
				nextErrors.categorias = "Selecciona al menos una categoría";
			if (descripcion && descripcion.length < 10) {
				nextErrors.descripcion = "Debe tener al menos 10 caracteres";
			}
		}

		if (currentStep === 2) {
			const hasEnabled = availability.some((slot) => slot.enabled);
			if (!hasEnabled)
				nextErrors.availability = "Debes seleccionar al menos un día disponible";
			availability.forEach((slot) => {
				if (!slot.enabled) return;
				const startMinutes = time12ToMinutes(slot.startTime, slot.startPeriod);
				if (startMinutes === null) {
					nextErrors.availability = `Hora de inicio inválida en ${slot.day} (ej: 9:00 AM)`;
					return;
				}
				const endMinutes = time12ToMinutes(slot.endTime, slot.endPeriod);
				if (endMinutes === null) {
					nextErrors.availability = `Hora de fin inválida en ${slot.day} (ej: 6:00 PM)`;
					return;
				}
				if (startMinutes >= endMinutes) {
					nextErrors.availability =
						"La hora de inicio debe ser menor a la de fin";
				}
			});
			if (!accepted) nextErrors.accepted = "Debes aceptar las políticas y términos";
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const prev = () => {
		setErrors({});
		setStep((s) => (s === 0 ? 0 : ((s - 1) as 0 | 1 | 2)));
	};
	const next = () => {
		if (step === 2) return;
		if (!validateStep(step)) return;
		setStep((s) => (s === 2 ? 2 : ((s + 1) as 0 | 1 | 2)));
	};

	const stepTitle = useMemo(() => {
		switch (step) {
			case 0:
				return {
					title: "Datos Personales",
					subtitle: "Completa tu información personal",
				};
			case 1:
				return {
					title: "Información del Negocio",
					subtitle: "Aplica si trabajas en empresa o de forma independiente",
				};
			case 2:
				return {
					title: "Horarios de Disponibilidad",
					subtitle: "Configura tus horarios de atención",
				};
			default:
				return {
					title: "Horarios de Disponibilidad",
					subtitle: "Configura tus horarios de atención",
				};
		}
	}, [step]);

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setServerError(null);
		setToastOpen(false);

		if (step < 2) {
			next();
			return;
		}

		if (!validateStep(2)) return;
		setSubmitting(true);
		try {
			const companyName =
				company.nombreEmpresa.trim() ||
				`${personal.nombre.trim()} ${personal.apellido.trim()}`.trim();

			const passwordToUse = isAdminMode ? generatePassword() : personal.password;

			await registerProvider({
				names: personal.nombre.trim(),
				lastNames: personal.apellido.trim(),
				password: passwordToUse,
				email: personal.email.trim(),
				phone: personal.telefono.trim(),
				address: personal.direccion.trim(),
				companyName,
				description: company.descripcion.trim() || undefined,
				categoryIds: company.categoryIds,
				availability,
			});

			// Limpiar datos guardados al finalizar registro
			localStorage.removeItem(LOCAL_KEY);

			if (isAdminMode) {
				setToastOpen(true);
			} else {
				setActivationScreenOpen(true);
			}
			onCreated?.();

			if (isAdminMode && successRedirectTo) {
				setTimeout(() => navigate(successRedirectTo), 3500);
			}
		} catch (err) {
			setServerError(
				err instanceof Error ? err.message : "No se pudo completar el registro",
			);
		} finally {
			setSubmitting(false);
		}
	};

	const successMessage = isAdminMode
		? `Proveedor creado exitosamente: ${personal.nombre.trim()}${personal.apellido.trim() ? ` ${personal.apellido.trim()}` : ""}.`
		: `Felicidades, se ha registrado exitosamente ${personal.nombre.trim()}${personal.apellido.trim() ? ` ${personal.apellido.trim()}` : ""} como proveedor, para activar su cuenta tiene que ponerse en contacto con soporte al cliente`;

	const handleActivationAccess = () => {
		if (redirectTimeoutRef.current !== null) {
			window.clearTimeout(redirectTimeoutRef.current);
			redirectTimeoutRef.current = null;
		}

		if (successRedirectTo) {
			navigate(successRedirectTo);
			return;
		}

		setActivationScreenOpen(false);
	};

	const cardClassName =
		"rounded-3xl border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.10)]" +
		(variant === "modal" ? " flex h-full flex-col" : "");
	const headerClassName =
		"px-6 py-4 sm:px-10" +
		(variant === "modal" ? " shrink-0 border-b border-slate-200" : "");
	const bodyClassName =
		"px-6 pb-8 sm:px-10" + (variant === "modal" ? " flex-1 overflow-auto py-6" : "");

	if (!isAdminMode && activationScreenOpen) {
		return (
			<div className='fixed inset-0 z-50 flex items-center justify-center bg-white px-4'>
				<div className='w-full max-w-130 text-center'>
					<h2 className='text-2xl font-semibold text-slate-800'>
						¡Activa tu cuenta!
					</h2>
					<p className='mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-600'>
						Estimado cliente como último paso, tienes que agendar una cita con
						nuestro equipo para activar tu cuenta de proveedor.
					</p>
					<button
						type='button'
						onClick={handleActivationAccess}
						className='mt-8 inline-flex h-12 min-w-70 items-center justify-center rounded-full bg-[#0B8FB7] px-10 text-sm font-medium text-white transition hover:bg-[#0A7FA3] cursor-pointer'
					>
						Ingresar
					</button>
				</div>
			</div>
		);
	}

	return (
		<>
			<SuccessToast
				open={toastOpen}
				title='Nuevo usuario'
				message={successMessage}
				onClose={() => setToastOpen(false)}
			/>
			<div className={cardClassName}>
				<div className={headerClassName}>
					<div className='flex justify-end'>
						<button
							type='button'
							aria-label='Cerrar'
							onClick={onClose}
							className='cursor-pointer rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
						>
							<X className='h-5 w-5' />
						</button>
					</div>
					<div className='mt-3'>
						<div className='mx-auto w-full max-w-5xl'>
							<WizardStepper step={step} total={3} />
						</div>
					</div>
				</div>

				<div className={bodyClassName}>
					<h2 className='text-center text-xl font-semibold text-slate-800'>
						{stepTitle.title}
					</h2>
					<p className='mt-1 text-center text-sm text-slate-500'>
						{stepTitle.subtitle}
					</p>

					<form onSubmit={onSubmit} className='mt-6 space-y-6'>
						{serverError ? (
							<div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
								{serverError}
							</div>
						) : null}

						{step === 0 ? (
							<StepPersonal
								personal={personal}
								setPersonal={setPersonal}
								errors={errors}
								clearError={clearError}
								showPassword={showPassword}
								onToggleShowPassword={() => setShowPassword((v) => !v)}
								isAdminMode={isAdminMode}
								inputBase={inputBase}
								inputWithIcon={inputWithIcon}
								errorText={errorText}
							/>
						) : null}

						{step === 1 ? (
							<StepCompany
								company={company}
								setCompany={setCompany}
								errors={errors}
								clearError={clearError}
								inputBase={inputBase}
								inputWithIcon={inputWithIcon}
								errorText={errorText}
							/>
						) : null}

						{step === 2 ? (
							<StepAvailability
								availability={availability}
								setAvailability={setAvailability}
								errors={errors}
								clearError={clearError}
								accepted={accepted}
								setAccepted={setAccepted}
							/>
						) : null}

						<div className='flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between'>
							<div>
								{step > 0 ? (
									<button
										type='button'
										onClick={prev}
										disabled={submitting}
										className='cursor-pointer rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed'
									>
										Anterior
									</button>
								) : null}
							</div>

							<div className='flex items-center gap-3'>
								{step < 2 ? (
									<button
										type='button'
										onClick={next}
										disabled={submitting}
										className='cursor-pointer rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
									>
										Siguiente
									</button>
								) : (
									<button
										type='submit'
										disabled={submitting || !accepted}
										className='cursor-pointer rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
									>
										{submitting ? "Enviando..." : "Finalizar"}
									</button>
								)}
							</div>
						</div>
					</form>
				</div>
			</div>
		</>
	);
}
export type {
	ProviderRegistrationWizardMode,
	ProviderRegistrationWizardVariant,
	ProviderRegistrationFormProps,
} from "../../../core/types/tsx/components/wizard/provider-registration/ProviderRegistrationForm.types";
